import { Payload } from 'recharts/types/component/DefaultLegendContent'
import { formatValueWithUnit, getLocalizedNumber, getLocalizedPercentage } from 'src/Libs/languages/functions/default'
import type { PieChartData } from 'src/Libs/chart-kit/pie-charts/components/pie-chart.types'
import { UnitTypes } from 'src/Libs/types'
import { TFunction } from 'i18next'
import { Theme } from '@mui/material'
import { stringWithSIPrefix, STATISTICS_SI_PREFIXES } from 'src/Libs/system-data-handler/utils/formatNumberWithSIPrefix'

/**
 * Returns a legend payload including the according.
 * @param data The pie chart data.
 * @param unitType The unit type to format the values (optional - defaults to percentage).
 */
export const getShortLegendPayload = (data: PieChartData[], unitType?: UnitTypes): Payload[] => {
  const { percentages } = calculateLegendValues(data)

  return data.map((item, index) => {
    const name = formatLegendItemName(item.name)
    let value = ''
    if (unitType !== undefined) {
      value = stringWithSIPrefix(item.value ?? 0, unitType, 1, undefined, STATISTICS_SI_PREFIXES)
    } else {
      value = getLocalizedPercentage(percentages[index]).trim()
    }

    return {
      id: item.key ?? item.name,
      type: 'square',
      value,
      color: item.color,
      dataKey: name,
    }
  })
}

/**
 * Returns a legend payload including price values.
 */
export const getSavingsLegendPayload = (data: PieChartData[], t?: TFunction, theme?: Theme): Payload[] => {
  const sum = data.reduce((partialSum, item) => partialSum + (item.value ?? 0), 0)
  if (sum > 9999) data.push({ name: t ? t('dashboard.editor.summary') : '', color: theme?.palette.primary.main ?? '', value: sum })

  const { values } = calculateLegendValues(data, true)

  return data.map((item, index) => {
    const name = formatLegendItemName(item.name)
    const savingsPriceInfo = formatValueWithUnit(getLocalizedNumber(values[index]), UnitTypes.euros)

    return {
      id: item.name,
      type: 'square',
      value: savingsPriceInfo,
      color: item.color,
      dataKey: name,
    }
  })
}

/**
 * Formats the name of a legend item based on the name itself.
 */
const formatLegendItemName = (name?: string): string => {
  return `${name}`
}

const EXPECTED_PERCENTAGE_SUM = 100

/**
 * Calculates kilo watt hour values and the respective percentages for each legend entry.
 */
const calculateLegendValues = (data: PieChartData[], hasPositiveValuesOnly?: boolean): { values: number[]; percentages: number[] } => {
  const totalSum = data.reduce((prev, curr) => {
    if (curr.value) return prev + curr.value
    return prev
  }, 0)

  const positiveTotalSum = data.reduce((sum, item) => {
    const value = item.value ?? 0
    return value > 0 ? sum + value : sum
  }, 0)

  const usedTotalSum = hasPositiveValuesOnly ? positiveTotalSum : totalSum
  const values: number[] = []
  const percentages: number[] = []

  data.forEach(item => {
    const value = roundValue(item.value ?? 0)
    values.push(value)
    percentages.push(calculatePercentage(value, usedTotalSum, hasPositiveValuesOnly))
  })

  const normalizedPercentages = normalizePercentages(percentages)

  return { values, percentages: normalizedPercentages }
}

/**
 * round value to two decimals
 */
const roundValue = (value: number): number => {
  return Math.round(value * 100) / 100
}

/**
 * calculates percentage of value in relation to total sum
 */
const calculatePercentage = (value: number, totalSum: number, hasPositiveValuesOnly?: boolean): number => {
  if (value === 0) return 0
  if (hasPositiveValuesOnly && value <= 0) return 0
  return Math.round(((value / totalSum) * 100 * 100) / 100)
}

/**
 * Checks if the sum of the percentages is meaningful (0% and 100% are excepted results)
 */
const normalizePercentages = (percentages: number[]): number[] => {
  const percentageSum = percentages.reduce((prev, curr) => prev + curr, 0)
  if (percentageSum !== EXPECTED_PERCENTAGE_SUM && percentageSum !== 0) {
    const largestPercentageValueIndex = percentages.indexOf(Math.max(...percentages))
    percentages[largestPercentageValueIndex] += EXPECTED_PERCENTAGE_SUM - percentageSum
  }

  return percentages
}
